import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/login';
import Feed from './components/feed';
import Admin from './components/admin';
import AdminWells from './components/admin-wells';
import WellDetails from './components/well-details';
import Register from './components/register';
import ResetPassword from './components/reset-password';
import VerifyEmail from './components/verify-email';
import ProtectedRoute from './components/ProtectedRoute';
import { startWebSocket } from './utils/websocket';

// Check if user is logged in for initial route
const getInitialRoute = () => {
  try {
    const userString = localStorage.getItem('user');
    if (!userString) return '/login';
    const user = JSON.parse(userString);
    if (user && user.isActive) {
      return user.role === 'ADMIN' ? '/admin' : '/feed';
    }
  } catch (error) {
    console.error('Error reading user from localStorage:', error);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  }
  return '/login';
};

// A minimal type for wells; adjust to your real shape if needed
type Well = { id: string; name?: string };

// Wrapper component to provide required props to AdminWells and open a modal
const AdminWellsWrapper: React.FC<
  Omit<React.ComponentProps<typeof AdminWells>, 'onViewDetails'>
> = (props) => {
  const [selectedWell, setSelectedWell] = useState<Well | null>(null);

  return (
    <>
      <AdminWells
        {...props}
        onViewDetails={(well: Well) => setSelectedWell(well)}
      />

      {/* Lightweight modal implementation */}
      {selectedWell && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setSelectedWell(null)}
        >
          <div
            className="max-w-3xl w-full bg-white rounded-lg shadow-xl p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl font-semibold">
                Well Details {selectedWell.name ? `— ${selectedWell.name}` : ''}
              </h2>
              <button
                className="px-3 py-1 rounded border"
                onClick={() => setSelectedWell(null)}
              >
                Close
              </button>
            </div>

            {/* If your WellDetails component can render without route params,
                you can place it here. Otherwise, keep this placeholder or
                swap in a modal-specific details component. */}
            <div className="text-sm text-gray-700">
              <p><strong>ID:</strong> {selectedWell.id}</p>
              {/* TODO: render richer details here (fetch by ID, etc.) */}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

function App() {
  useEffect(() => {
    startWebSocket();
  }, []);

  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />

          {/* Protected Routes */}
          <Route
            path="/feed"
            element={
              <ProtectedRoute allowedRoles={['USER', 'ADMIN']}>
                <Feed />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <Admin />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/wells"
            element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <AdminWellsWrapper />
              </ProtectedRoute>
            }
          />

          {/* Keep this if you also support direct-linking to a details page */}
          <Route
            path="/admin/wells/:id"
            element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <WellDetails />
              </ProtectedRoute>
            }
          />

          {/* Default Route - Redirect to appropriate page */}
          <Route path="/" element={<Navigate to={getInitialRoute()} replace />} />

          <Route path="/health" element={<span>ok</span>} />

          {/* Catch all route - redirect to appropriate page */}
          <Route path="*" element={<Navigate to={getInitialRoute()} replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;